import axios from 'axios';
import { logger } from '@fiverr-private/obs';
import pathfinder from '@fiverr-private/pathfinder';
import { raiseToast } from '@fiverr-private/toast';
import { translate } from '@fiverr-private/i18n-react';
import { getContext } from '@fiverr-private/fiverr_context';
import { ComplianceType, QuestionsSubmitData } from '../components/ComplianceModal/types';
import { ConfigResponse, FetchedOrganizationPackagesData } from '../types/complianceTypes';

const raiseErrorToast = (complianceType: ComplianceType) => {
    raiseToast({
        location: 'top',
        type: 'error',
        message: translate(`compliance.toast.${complianceType}.error`),
    });
};

export const getSellerConfigData = async ({ requeseteeId }: { requeseteeId: number }): Promise<ConfigResponse> => {
    const { userId } = getContext();
    const url = pathfinder(
        'compliance_configs_by_requester_id_api',
        { requester_id: userId || 1 }, // Not used by BE, but required in route params. // TODO: Remove once the route is updated
        {
            query: {
                requeseteeId: requeseteeId.toString(),
            },
        }
    );
    try {
        const response = await axios.get<ConfigResponse>(url);
        return response.data;
    } catch (error) {
        logger.error(error as Error, {
            description: `Failed to get config data for requeseteeId - ${requeseteeId}], route: [${url}]`,
        });

        throw error;
    }
};

export const getSellerComplianceConfigData = async ({
    requeseteeId,
}: {
    requeseteeId: number;
}): Promise<ConfigResponse> => {
    const url = pathfinder('compliance_configs_by_requestor_and_requestee_ids', {
        requestee_id: requeseteeId,
    });
    try {
        const response = await axios.get<ConfigResponse>(url);
        return response.data;
    } catch (error) {
        logger.warn(error as Error, {
            description: `Failed to get config data for requeseteeId - ${requeseteeId}], route: [${url}]`,
        });

        throw error;
    }
};

export const getOrganizationPackagesData = async (): Promise<FetchedOrganizationPackagesData> => {
    const url = pathfinder('compliance_packages_data_by_organization_id');
    try {
        const response = await axios.get<FetchedOrganizationPackagesData>(url);
        return response.data;
    } catch (error) {
        logger.warn(error as Error, {
            description: `Failed to get organization packages data, route: [${url}]`,
        });

        throw error;
    }
};

export const getOrganizationComplianceData = async (organizationId: string, sellerIds?: number[]) => {
    const url = pathfinder('query_organization_seller_data_by_ids');
    try {
        const response = await axios.post(url, {
            sellerIds: sellerIds || [],
        });
        return response.data;
    } catch (error) {
        logger.error(error as Error, {
            description: `Failed to get compliance data for : [organizationId - ${organizationId}], route: [${url}]`,
        });

        throw error;
    }
};

export const getBackgroundCheckResultFile = async ({
    requester_id,
    externalVendorId,
}: {
    requester_id: number;
    externalVendorId: string;
}) => {
    const url = pathfinder(
        'results_file_by_external_id_api',
        { external_id: externalVendorId },
        { query: { requester_id: requester_id.toString() } }
    );

    try {
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        logger.error(error as Error, {
            description: `Failed to get results file for : [external_id - ${externalVendorId}, requester_id: ${requester_id}], route: [${url}]`,
        });
        raiseToast({
            location: 'top',
            type: 'error',
            message: translate(`compliance.toast.background_check.reportError`),
        });
        throw error;
    }
};

export const createPaymentSession = async ({
    complianceType,
    currency,
    redirectUrl,
    source,
    requesteeId,
    discountInPercents,
    auditAnswers,
    templateIds,
}: {
    complianceType: ComplianceType;
    currency?: string;
    redirectUrl?: string;
    source?: string;
    requesteeId: number;
    discountInPercents?: number;
    auditAnswers?: QuestionsSubmitData;
    templateIds?: string[];
}) => {
    const url = pathfinder('create_compliance_payment_session_api');
    try {
        const response = await axios.post(
            url,
            {
                packageType: complianceType,
                currency,
                redirectUrl,
                source,
                requesteeId,
                discountInPercents,
                auditAnswers,
                templateIds,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );
        return response.data;
    } catch (error) {
        logger.error(error as Error, {
            description: `Failed to create payment session : [complianceType - ${complianceType}], route: [${url}]`,
        });
        raiseErrorToast(complianceType);

        throw error;
    }
};
